import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "bootstrap/dist/css/bootstrap.min.css";
import React from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { PersistGate } from "redux-persist/integration/react";
import { setLocale } from "yup";
import { en, es } from "yup-locales";
import App from "./App.jsx";
import "./i18n";
import "./index.css";
import { logout } from "./redux/features/authSlice.js";
import { persistor, store } from "./redux/store.js";
import { toastAlert } from "./utils/SweetAlert.js";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

let lang = localStorage.getItem("i18nextLng");
setLocale(lang === "es" ? es : en);

const handleQueryError = (error) => {
  if (error?.response) {
    const { status, data } = error.response;

    let detailMessage = "";
    if (Array.isArray(data?.data) && data.data.length > 0) {
      detailMessage = data.data.join(", ");
    } else if (typeof data?.data === "string" && data.data) {
      detailMessage = data.data;
    }

    const message = detailMessage
      ? `${data?.message ? data.message + ": " : ""}${detailMessage}`
      : data?.message || data?.error || "Something went wrong. Please try again.";

    // Auth-related errors
    if (status === 401 || status === 403) {
      store.dispatch(logout());
      queryClient.clear();
      toastAlert("error", message || "Unauthorized! Please login again.");
      window.location.href = "/login-admin";
      return;
    }

    switch (status) {
      case 400:
        toastAlert("error", message || "Bad Request. Please check your input.");
        break;
      case 404:
        toastAlert("error", message || "Requested resource not found.");
        break;
      case 500:
        toastAlert("error", message || "Internal Server Error. Please try again later.");
        break;
      default:
        toastAlert("error", message);
    }
  } else {
    toastAlert("error", error?.message || "Network Error. Please check your connection.");
  }
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Admin data can change outside the current screen. Always fetch fresh
      // data when a query is mounted again instead of reusing inactive cache.
      staleTime: 0,
      gcTime: 0,
      throwOnError: handleQueryError,
    },
    mutations: {
      onError: handleQueryError,
    },
  },
});

ReactDOM.createRoot(document.getElementById("root")).render(
  <QueryClientProvider client={queryClient}>
    <DndProvider backend={HTML5Backend}>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </PersistGate>
      </Provider>
    </DndProvider>
  </QueryClientProvider>
);
